<template>
  <Teleport to="body">
    <Transition name="modal-autenticacao-2fa" @after-leave="onAfterLeave">
      <dialog
        v-if="isOpen"
        ref="dialogRef"
        class="modal-autenticacao-2fa__panel"
        :aria-labelledby="titleId"
        @cancel="onDialogCancel"
        @click="onDialogBackdropClick"
      >
        <div class="modal-autenticacao-2fa__content">
          <div class="modal-autenticacao-2fa__handle-wrap" aria-hidden="true">
            <span class="modal-autenticacao-2fa__handle" />
          </div>

          <button
            type="button"
            class="modal-autenticacao-2fa__close"
            aria-label="Fechar"
            @click="dismissModal"
          >
            <CloseIcon />
          </button>

          <div class="modal-autenticacao-2fa__header">
            <img
              src="@/assets/images/icons/shield-checkmark.svg"
              alt=""
              class="modal-autenticacao-2fa__icon"
            >
            <h2 :id="titleId" class="modal-autenticacao-2fa__title">
              Confirme o código
            </h2>
          </div>

          <p class="modal-autenticacao-2fa__message">
            Para ver detalhes da negociação, confirme o código que acabamos de enviar para o seu e-mail
            <strong>{{ email }}</strong>
          </p>

          <div :class="['modal-autenticacao-2fa__digits', { 'modal-autenticacao-2fa__digits--error': hasError }]">
            <SplitDigitsInput
              v-model="digits"
              :disable-input="isConfirmandoOtp"
              :variant="hasError ? 'error' : 'default'"
              auto-focus
              @focus-next="confirmButton?.focus()"
              @interacted="onInputInteraction"
            />
          </div>

          <p v-if="textErr" class="modal-autenticacao-2fa__error">
            {{ textErr }}
            <button
              v-if="canRequestOtpAgain"
              type="button"
              class="modal-autenticacao-2fa__resend"
              @click="clickRequestOtp"
            >
              Reenviar código.
            </button>
          </p>
          <p v-else-if="hasRequestedNewOtp" class="modal-autenticacao-2fa__hint">
            {{ newOtpRequestMsg }}
          </p>
          <p v-if="syncError" class="modal-autenticacao-2fa__error">
            {{ syncError }}
          </p>

          <button
            v-if="podeRetentarPosOtp"
            type="button"
            class="button modal-autenticacao-2fa__confirm"
            :disabled="isConfirmandoOtp"
            @click="retryPosOtp"
          >
            Tentar novamente
          </button>
          <button
            v-else
            ref="confirmButton"
            type="button"
            class="button modal-autenticacao-2fa__confirm"
            :disabled="isDisabled"
            @click="submit"
          >
            Confirmar
          </button>

          <p v-if="!codigoExpirado" class="modal-autenticacao-2fa__expire-label">
            O código expira em...
          </p>
          <p
            :class="[
              'modal-autenticacao-2fa__timer',
              { 'modal-autenticacao-2fa__timer--error': hasError },
            ]"
          >
            {{ txtTempoRestante }}
          </p>
        </div>
      </dialog>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, nextTick, onUnmounted, ref, watch } from 'vue';
import SplitDigitsInput from '@/blocks/deeplink-login/components/SplitDigitsInput.vue';
import { isAutenticacao2faPosOtpError, useAutenticacao2faGate } from '@/composables/useAutenticacao2faGate';
import logger from '@/logger';
import ApiErrorCode from '@/models/enums/ApiErrorCode';
import ApiError from '@/models/resources/ApiError';
import { acertoAcessouAutenticacao, acertoClickConfirmarAutenticacao } from '@/modules/events/acerto';
import AuthService from '@/services/AuthService';
import {
  ga4AcessouAutenticacao,
  ga4ClickConfirmarCodigoInvalido,
  ga4ClickConfirmarCodigoSucesso,
  ga4ClickConfirmarCodigoVazio,
  ga4ClickReenviarCodigo,
  ga4CodigoExpirado,
} from '@/services/GA4Service';
import SessionService from '@/services/SessionService';
import useAutenticacao2faModalStore from '@/stores/useAutenticacao2faModalStore';
import CloseIcon from '~icons/ic/round-close';

const store = useAutenticacao2faModalStore();
const { isOpen, api } = storeToRefs(store);
const { concluirAposOtpValido, fecharOtp } = useAutenticacao2faGate();

const titleId = 'modal-autenticacao-2fa-title';
const dialogRef = ref<HTMLDialogElement | null>(null);
let triggerElement: HTMLElement | null = null;

const email = computed(() => SessionService.token?.payload?.emailOfuscado ?? '');
const digits = ref<string[]>(new Array<string>(5).fill(''));
const confirmButton = ref<HTMLButtonElement>();
const isConfirmandoOtp = ref(false);
const isSolicitandoOtp = ref(false);
const hasRequestedNewOtp = ref(false);
const syncError = ref('');
const dataExpiracao = ref(0);
const tempoExpiracaoRestante = ref(0);

type ErrTextCode = 'empty' | 'incomplete' | 'invalid' | 'expired' | 'unknown';
const errTextCode = ref<ErrTextCode>('empty');

const textErr = computed(() => {
  if (errTextCode.value === 'empty') return '';
  const map: Record<Exclude<ErrTextCode, 'empty'>, string> = {
    incomplete: 'Informe o código para continuar.',
    invalid: 'Código inválido. Verifique o código enviado por e-mail e tente novamente.',
    expired: 'O código expirou.',
    unknown: 'Ocorreu um erro.',
  };
  return map[errTextCode.value];
});
const hasError = computed(() => !!textErr.value);
const canRequestOtpAgain = computed(() => ['expired', 'unknown'].includes(errTextCode.value));
const newOtpRequestMsg = computed(() =>
  isSolicitandoOtp.value ? 'Reenviando código...' : 'Código reenviado. Verifique o e-mail.'
);
const codigoExpirado = computed(() => tempoExpiracaoRestante.value <= 0);
const txtTempoRestante = computed(() => {
  if (codigoExpirado.value) return '00:00';
  const totalSeconds = Math.ceil(tempoExpiracaoRestante.value / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
});
const isDisabled = computed(() => codigoExpirado.value || isConfirmandoOtp.value);
const credor = computed(() => api.value ?? 'portal');
const podeRetentarPosOtp = computed(() => !!syncError.value);

let contadorTimeoutId: ReturnType<typeof setInterval> | undefined;

function isFocusable(el: HTMLElement): boolean {
  if (el.hasAttribute('disabled')) {
    return false;
  }
  if (el.getAttribute('aria-hidden') === 'true') {
    return false;
  }
  return el.tabIndex >= 0 || el.matches('button, [href], input, select, textarea');
}

function captureTriggerFocus() {
  const active = document.activeElement;
  triggerElement = active instanceof HTMLElement ? active : null;
}

function restoreTriggerFocus() {
  const el = triggerElement;
  triggerElement = null;
  if (!el || !document.contains(el) || !isFocusable(el)) {
    return;
  }
  el.focus();
}

function openDialogModal() {
  const dialog = dialogRef.value;
  if (!dialog || dialog.open) {
    return;
  }

  if (typeof dialog.showModal === 'function') {
    dialog.showModal();
    return;
  }

  dialog.setAttribute('open', '');
}

function closeDialogModal() {
  const dialog = dialogRef.value;
  if (!dialog) {
    return;
  }

  if (typeof dialog.close === 'function' && dialog.open) {
    dialog.close();
    return;
  }

  dialog.removeAttribute('open');
}

function dismissModal() {
  if (contadorTimeoutId) clearInterval(contadorTimeoutId);
  fecharOtp();
  resetFormState();
}

function onDialogCancel(event: Event) {
  event.preventDefault();
  if (isOpen.value) {
    dismissModal();
  }
}

function iniciarContagemRegressiva() {
  if (contadorTimeoutId) clearInterval(contadorTimeoutId);

  contadorTimeoutId = setInterval(() => {
    tempoExpiracaoRestante.value = dataExpiracao.value - Date.now();
    if (codigoExpirado.value) {
      ga4CodigoExpirado(credor.value);
      if (contadorTimeoutId) clearInterval(contadorTimeoutId);
      errTextCode.value = 'expired';
    }
  }, 100);
}

function resetFormState() {
  digits.value = new Array<string>(5).fill('');
  errTextCode.value = 'empty';
  hasRequestedNewOtp.value = false;
  syncError.value = '';
  isConfirmandoOtp.value = false;
}

function mensagemPosOtp(error: unknown): string {
  if (isAutenticacao2faPosOtpError(error) && error.kind === 'navigate') {
    return 'Não foi possível abrir a página. Tente novamente.';
  }
  return 'Não foi possível confirmar a autenticação. Tente novamente.';
}

function registrarFalhaPosOtp(error: unknown) {
  logger.warn('Falha após confirmação de OTP no modal 2FA', error);
  syncError.value = mensagemPosOtp(error);
}

function requestOtp() {
  if (SessionService.is2faAuthorized) {
    return;
  }

  isSolicitandoOtp.value = true;
  AuthService.solicitarOTP()
    .then(({ tempoExpiracaoOtp }) => {
      const ttlMs = tempoExpiracaoOtp * 1000;
      dataExpiracao.value = Date.now() + ttlMs;
      tempoExpiracaoRestante.value = ttlMs;
      iniciarContagemRegressiva();
      errTextCode.value = 'empty';
    })
    .catch((error: unknown) => {
      logger.warn('Falha ao solicitar OTP', error);
      errTextCode.value = 'unknown';
    })
    .finally(() => {
      isSolicitandoOtp.value = false;
    });
}

async function retryPosOtp() {
  if (isConfirmandoOtp.value) return;
  syncError.value = '';
  isConfirmandoOtp.value = true;
  try {
    await concluirAposOtpValido();
    resetFormState();
  } catch (error) {
    registrarFalhaPosOtp(error);
  } finally {
    isConfirmandoOtp.value = false;
  }
}

function onDialogBackdropClick(event: MouseEvent) {
  if (event.target === dialogRef.value) {
    dismissModal();
  }
}

function submit() {
  if (isDisabled.value) return;

  acertoClickConfirmarAutenticacao();
  const ga4DataUa = `click-conv-portal-codigo-${credor.value}`;

  if (digits.value.some(d => !d)) {
    ga4ClickConfirmarCodigoVazio(credor.value, ga4DataUa);
    errTextCode.value = 'incomplete';
    return;
  }

  errTextCode.value = 'empty';
  syncError.value = '';
  isConfirmandoOtp.value = true;

  AuthService.confirmarOTP(`${digits.value.join('')}`)
    .then(async () => {
      ga4ClickConfirmarCodigoSucesso(credor.value, ga4DataUa);
      try {
        await concluirAposOtpValido();
        resetFormState();
      } catch (error) {
        registrarFalhaPosOtp(error);
      }
    })
    .catch((err: unknown) => {
      const errors = new ApiError(err);
      if (errors.is(ApiErrorCode.CODIGO_OTP_INCORRETO)) {
        errTextCode.value = 'invalid';
        ga4ClickConfirmarCodigoInvalido(credor.value, ga4DataUa);
        return;
      }
      if (errors.is(ApiErrorCode.CODIGO_OTP_EXPIRADO)) {
        errTextCode.value = 'expired';
        return;
      }
      logger.warn('Falha ao confirmar OTP', err);
      errTextCode.value = 'unknown';
    })
    .finally(() => {
      isConfirmandoOtp.value = false;
    });
}

function clickRequestOtp() {
  errTextCode.value = 'empty';
  hasRequestedNewOtp.value = true;
  requestOtp();
  ga4ClickReenviarCodigo(credor.value, `click-conv-portal-reenviar-codigo-${credor.value}`);
}

function onInputInteraction({ key }: { key: string }) {
  if (errTextCode.value !== 'expired') errTextCode.value = 'empty';
  if (key === 'Enter') submit();
}

watch(isOpen, async open => {
  if (!open) {
    if (contadorTimeoutId) clearInterval(contadorTimeoutId);
    return;
  }

  if (SessionService.is2faAuthorized) {
    if (!isConfirmandoOtp.value) {
      fecharOtp();
    }
    return;
  }

  captureTriggerFocus();
  resetFormState();
  await nextTick();
  openDialogModal();
  requestOtp();
  ga4AcessouAutenticacao(credor.value);
  acertoAcessouAutenticacao();
});

function onAfterLeave(el: Element) {
  const dialog = el as HTMLDialogElement;
  if (typeof dialog.close === 'function' && dialog.open) {
    dialog.close();
  } else {
    dialog.removeAttribute('open');
  }
  restoreTriggerFocus();
}

onUnmounted(() => {
  if (contadorTimeoutId) clearInterval(contadorTimeoutId);
  closeDialogModal();
  restoreTriggerFocus();
});
</script>

<style lang="scss" scoped>
@use '@/assets/styles/vars' as *;

.modal-autenticacao-2fa__panel {
  display: block;
  position: fixed;
  inset: auto 0 0;
  z-index: 1200;
  width: 100%;
  max-width: 420px;
  margin: 0 auto;
  border: none;
  padding: 0;
  background: transparent;

  @include responsive($tablet) {
    inset: 50% auto auto 50%;
    transform: translate(-50%, -50%);
  }

  &::backdrop {
    background: rgba(0, 0, 0, 0.45);
  }
}

.modal-autenticacao-2fa__content {
  max-height: 92vh;
  overflow-y: auto;
  padding: $space-sm $space-sm $space-lg;
  background: var(--neutral-FF, #fff);
  border-radius: 20px 20px 0 0;
  box-shadow: 0 -8px 30px rgba(0, 0, 0, 0.12);

  @include responsive($tablet) {
    border-radius: $border-radius;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.18);
  }
}

.modal-autenticacao-2fa__handle-wrap {
  display: flex;
  justify-content: center;
  margin-bottom: $space-xs;

  @include responsive($tablet) {
    display: none;
  }
}

.modal-autenticacao-2fa__handle {
  width: 40px;
  height: 5px;
  border-radius: 5px;
  background: var(--acerto-colors-neutral-400, #c8cdd6);
}

.modal-autenticacao-2fa__close {
  position: absolute;
  top: $space-sm;
  right: $space-sm;
  border: none;
  background: transparent;
  color: var(--acerto-colors-neutral-500, #9ca3af);
  cursor: pointer;
  padding: $space-xxxs;
}

.modal-autenticacao-2fa__header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: $space-xxs;
  margin-bottom: $space-xs;
}

.modal-autenticacao-2fa__icon {
  width: 48px;
  height: 48px;
}

.modal-autenticacao-2fa__title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--acerto-colors-neutral-800, #0b1523);
  text-align: center;
}

.modal-autenticacao-2fa__message {
  margin: 0 0 $space-sm;
  font-size: 1rem;
  line-height: 1.45;
  text-align: center;
  color: var(--acerto-colors-neutral-700, #374151);
}

.modal-autenticacao-2fa__digits {
  margin-bottom: $space-sm;
}

.modal-autenticacao-2fa__error {
  margin: 0 0 $space-xs;
  font-size: 0.875rem;
  color: #d71f1e;
  text-align: center;
}

.modal-autenticacao-2fa__hint {
  margin: 0 0 $space-xs;
  font-size: 0.875rem;
  text-align: center;
  color: var(--acerto-colors-neutral-600, #6b7280);
}

.modal-autenticacao-2fa__resend {
  border: none;
  background: transparent;
  color: inherit;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
}

.modal-autenticacao-2fa__confirm {
  width: 100%;
  margin-bottom: $space-sm;
}

.modal-autenticacao-2fa__expire-label {
  margin: 0;
  text-align: center;
  font-size: 0.875rem;
  color: var(--acerto-colors-neutral-600, #6b7280);
}

.modal-autenticacao-2fa__timer {
  margin: $space-xxxs 0 0;
  text-align: center;
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--acerto-colors-neutral-800, #0b1523);

  &--error {
    color: #d71f1e;
  }
}

.modal-autenticacao-2fa-enter-active,
.modal-autenticacao-2fa-leave-active {
  transition:
    opacity $animation-duration,
    transform $animation-duration;
}

.modal-autenticacao-2fa-enter-from,
.modal-autenticacao-2fa-leave-to {
  opacity: 0;
  transform: translateY(100%);

  @include responsive($tablet) {
    transform: translate(-50%, calc(-50% + 16px)) scale(0.98);
  }
}
</style>
